import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { detailHref } from "@/lib/insights";

export interface NextEvent {
  title: string;
  summary: string;
  href: string;
  dateLabel: string;
  isVirtual: boolean;
  location: string | null;
}

export function useNextEvent(options: { upcomingOnly?: boolean } = {}) {
  const upcomingOnly = !!options.upcomingOnly;
  return useQuery({
    queryKey: ["next-event-promo", upcomingOnly ? "upcoming" : "any"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<NextEvent | null> => {
      const nowIso = new Date().toISOString();
      // An event stays "current" until it ends (falls back to its start when no end date).
      const base = supabase
        .from("insights_public")
        .select("title, summary, slug, kind, event_starts_at, event_ends_at, event_timezone, is_virtual, location")
        .eq("status", "published")
        .eq("kind", "event");

      const { data: upcomingRows, error: e1 } = await base
        .or(`event_ends_at.gte.${nowIso},and(event_ends_at.is.null,event_starts_at.gte.${nowIso})`)
        .order("event_starts_at", { ascending: true })
        .limit(1);
      if (e1) throw e1;

      let row = (upcomingRows ?? [])[0] ?? null;
      if (!row && !upcomingOnly) {
        const { data: past, error: e2 } = await supabase
          .from("insights_public")
          .select("title, summary, slug, kind, event_starts_at, event_ends_at, event_timezone, is_virtual, location")
          .eq("status", "published")
          .eq("kind", "event")
          .order("event_starts_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (e2) throw e2;
        row = past;
      }
      if (!row) return null;

      const d = row.event_starts_at ? new Date(row.event_starts_at) : null;
      // Format in UTC so a visitor's local timezone never shifts the date a day back.
      const dateLabel = d
        ? d.toLocaleDateString("en-GB", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
          })
        : "Date to be confirmed";


      return {
        title: row.title,
        summary: row.summary ?? "",
        href: detailHref({ kind: "event", slug: row.slug }),
        dateLabel,
        isVirtual: !!row.is_virtual,
        location: row.location ?? null,
      };
    },
  });
}
